import { useState, ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { Snackbar, Alert } from '@mui/material'
import {
    MutationCache,
    QueryCache,
    QueryClient,
    QueryClientProvider as TanstackQueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { getSSOLoginUrl } from '@/Shared/utils/getSSOLoginUrl'

export function QueryClientProvider({ children }: { children: ReactNode }) {
    const intl = useIntl()
    const [alertMessage, setAlertMessage] = useState<string | null>(null)

    const onError = (error: { status?: number; payload?: string }) => {
        const { status, payload = '' } = error

        if (status === 401) {
            window.location.assign(getSSOLoginUrl())
            return
        }

        if (status === 403) {
            setAlertMessage(
                intl.formatMessage(
                    { defaultMessage: 'Forbidden operation: {details}' },
                    { details: payload },
                ),
            )
            return
        }
    }

    const [queryClient] = useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        retry: false,
                        gcTime: 0,
                        refetchOnWindowFocus: false,
                    },
                },
                // @ts-expect-error onError is not a valid property of QueryCacheOptions
                queryCache: new QueryCache({ onError }),
                // @ts-expect-error onError is not a valid property of MutationCacheOptions
                mutationCache: new MutationCache({ onError }),
            }),
    )

    const handleClose = () => {
        setAlertMessage(null)
    }

    return (
        <TanstackQueryClientProvider client={queryClient}>
            {children}
            <ReactQueryDevtools initialIsOpen={true} />
            <Snackbar
                open={Boolean(alertMessage)}
                autoHideDuration={5000}
                onClose={handleClose}
                sx={{
                    '&': {
                        right: '24px',
                        left: 'auto',
                    },
                }}
            >
                <Alert onClose={handleClose} severity="error" variant="filled">
                    {alertMessage}
                </Alert>
            </Snackbar>
        </TanstackQueryClientProvider>
    )
}
